import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import toast, {Toaster} from "react-hot-toast";
import {ShimmerThumbnail} from "react-shimmer-effects";
import DatePicker from "react-datepicker";
import axios from "axios";
import ReactApexChart from "react-apexcharts";
import moment from "moment/moment";
import {Accordion, Button} from "react-bootstrap";
import {useLocation} from "react-router-dom";
import {connectMQTT, disconnectMQTT, subscribeTopic, unsubscribeTopic,} from "../../services/mqttService";

const numberOrDefault = (value, fallback = 0) => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  const parsed = Number(value);
  return Number.isNaN(parsed) ? fallback : parsed;
};

const formattedValue = (value, digits = 1, suffix = "") => {
  const numeric = numberOrDefault(value, null);
  if (numeric === null) {
    return "-";
  }

  if (typeof digits === "number") {
    const rounded = Number(numeric.toFixed(digits));
    return `${rounded}${suffix}`;
  }

  return `${numeric}${suffix}`;
};

const INITIAL_PERFORMANCE_METRICS = {
  customerSatisfaction: null,
  averageServedTime: null,
  averageWaitingTime: null,
  ticketCount: null,
};

const INITIAL_WEIGHT_DISTRIBUTION = {
  cf: null,
  om: null,
  fpt: null,
};

const INITIAL_SCORE_CARDS = {
  customerFeedback: null,
  operationalMetrics: null,
  feedbackPerTicket: null,
  finalPerformance: null,
};

const INITIAL_FINAL_BREAKDOWN = {
  cf: null,
  om: null,
  fpt: null,
};

const PIE_DATA_STORAGE_PREFIX = "feedback_dashboard_weight_distribution";

const getPieStorageKey = (branchId) =>
  `${PIE_DATA_STORAGE_PREFIX}_${branchId ?? "global"}`;

const loadStoredWeightDistribution = (branchId) => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const storedValue = localStorage.getItem(getPieStorageKey(branchId));
    if (!storedValue) {
      return null;
    }

    const parsed = JSON.parse(storedValue);
    if (!parsed || typeof parsed !== "object") {
      return null;
    }

    const { cf, om, fpt } = parsed;
    return {
      cf: toNumberOrNull(cf),
      om: toNumberOrNull(om),
      fpt: toNumberOrNull(fpt),
    };
  } catch (error) {
    console.warn("Unable to parse stored weight distribution", error);
    return null;
  }
};

const weightsAreEqual = (a, b) => {
  if (!a || !b) {
    return false;
  }

  return ["cf", "om", "fpt"].every(
    (key) => toNumberOrNull(a[key]) === toNumberOrNull(b[key])
  );
};

const timeStringToMinutes = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (typeof value === "number") {
    return value;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    const timeParts = trimmed.split(":");

    if (timeParts.length === 3) {
      const [hours, minutes, seconds] = timeParts.map((part) => Number(part));
      if (
        !Number.isNaN(hours) &&
        !Number.isNaN(minutes) &&
        !Number.isNaN(seconds)
      ) {
        return hours * 60 + minutes + seconds / 60;
      }
    }

    const numeric = Number(trimmed);
    if (!Number.isNaN(numeric)) {
      return numeric;
    }
  }

  return null;
};

const firstAvailable = (...values) =>
  values.find(
    (value) => value !== undefined && value !== null && value !== ""
  );

const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

const useAnimatedNumber = (target, options = {}) => {
    const {duration = 600} = options;
    const targetValue =
        target === null || target === undefined || Number.isNaN(target)
            ? null
            : target;

    const previousValueRef = useRef(targetValue);
    const animationFrameRef = useRef(null);
    const startTimeRef = useRef(null);
    const [displayValue, setDisplayValue] = useState(targetValue);

    useEffect(() => {
        const hasWindow = typeof window !== "undefined";
        const requestFrame = hasWindow
            ? window.requestAnimationFrame.bind(window)
            : null;
        const cancelFrame = hasWindow
            ? window.cancelAnimationFrame.bind(window)
            : null;

        if (animationFrameRef.current && cancelFrame) {
            cancelFrame(animationFrameRef.current);
        }
        animationFrameRef.current = null;
        startTimeRef.current = null;

        if (targetValue === null) {
            previousValueRef.current = null;
            setDisplayValue(null);
            return undefined;
        }

        const startingValue =
            previousValueRef.current === null ||
            previousValueRef.current === undefined ||
            Number.isNaN(previousValueRef.current)
                ? targetValue
                : previousValueRef.current;

        const difference = targetValue - startingValue;

        if (!Number.isFinite(startingValue) || Math.abs(difference) < 0.0001) {
            previousValueRef.current = targetValue;
            setDisplayValue(targetValue);
            return undefined;
        }

        const animate = (timestamp) => {
            if (startTimeRef.current === null) {
                startTimeRef.current = timestamp;
            }

            const progress = Math.min(
                (timestamp - startTimeRef.current) / duration,
                1
            );
            const easedProgress = easeOutCubic(progress);
            const nextValue = startingValue + difference * easedProgress;
            setDisplayValue(nextValue);

            if (progress < 1 && requestFrame) {
                animationFrameRef.current = requestFrame(animate);
            } else {
                previousValueRef.current = targetValue;
            }
        };

        if (requestFrame) {
            animationFrameRef.current = requestFrame(animate);
        } else {
            const fallbackStart = Date.now();
            const fallbackInterval = setInterval(() => {
                const progress = Math.min((Date.now() - fallbackStart) / duration, 1);
                const easedProgress = easeOutCubic(progress);
                const nextValue = startingValue + difference * easedProgress;
                setDisplayValue(nextValue);

                if (progress >= 1) {
                    clearInterval(fallbackInterval);
                    previousValueRef.current = targetValue;
                }
            }, 16);

            return () => {
                clearInterval(fallbackInterval);
            };
        }

        return () => {
            if (animationFrameRef.current && cancelFrame) {
                cancelFrame(animationFrameRef.current);
            }
        };
    }, [duration, targetValue]);

    return displayValue;
};

const AnimatedNumber = ({
                            value,
                            digits = 0,
                            suffix = "",
                            fallback = "-",
                        }) => {
    const targetValue = toNumberOrNull(value);
    const animatedValue = useAnimatedNumber(targetValue, {duration: 700});

    if (targetValue === null || animatedValue === null) {
        return <>{fallback}</>;
    }

    if (typeof digits === "number") {
        const rounded = Number(animatedValue.toFixed(digits));
        return <>{`${rounded}${suffix}`}</>;
    }

    return <>{`${animatedValue}${suffix}`}</>;
};

const AnimatedTicketCount = ({teller, total, fallback = "-"}) => {
    const tellerTarget = toNumberOrNull(teller);
    const totalTarget = toNumberOrNull(total);

    const animatedTeller = useAnimatedNumber(tellerTarget, {duration: 700});
    const animatedTotal = useAnimatedNumber(totalTarget, {duration: 700});

    if (tellerTarget === null && totalTarget === null) {
        return <>{fallback}</>;
    }

    const formatCount = (target, animated) => {
        if (target === null || animated === null) {
            return "-";
        }

        return Math.round(animated);
    };

    return (
        <>
            {formatCount(tellerTarget, animatedTeller)} /{" "}
            {formatCount(totalTarget, animatedTotal)}
        </>
    );
};

const FeedbackPerformanceDashboard = () => {
  const [messages, setMessages] = useState([]);

  const location = useLocation();

  const { sDate, eDate } = location.state || {};
  const baseUrl = localStorage.getItem("API_URL") || "";
  const token = localStorage.getItem("user_token") || "";
  const userDetails =
    JSON.parse(localStorage.getItem("user_details") || "{}") || {};

  const feedbackDashboardUpdate = `feedback/dashboard/branch`;

  const [kiosks, setKiosks] = useState([]);
  const [allDataLoaded, setAllDataLoaded] = useState(true);
  const [feedbackStats, setFeedbackStats] = useState(null);
  const [queueAgentsData, setQueueAgentsData] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [ticketCounts, setTicketCounts] = useState({
    teller: null,
    total: null,
  });

  const [performanceMetrics, setPerformanceMetrics] = useState({
    ...INITIAL_PERFORMANCE_METRICS,
  });
  const [weightDistribution, setWeightDistribution] = useState(() => {
    const initialBranchId = userDetails?.bid;
    const storedWeights = loadStoredWeightDistribution(initialBranchId);

    if (storedWeights) {
      return {
        ...INITIAL_WEIGHT_DISTRIBUTION,
        ...storedWeights,
      };
    }

    return {
      ...INITIAL_WEIGHT_DISTRIBUTION,
    };
  });
  const [scoreCards, setScoreCards] = useState({
    ...INITIAL_SCORE_CARDS,
  });
  const [finalBreakdown, setFinalBreakdown] = useState({
    ...INITIAL_FINAL_BREAKDOWN,
  });
  const [startDate, setStartDate] = useState(() =>
    moment().startOf("month").toDate()
  );
  const [endDate, setEndDate] = useState(() =>
    moment().endOf("month").toDate()
  );
  const [selectedMonth, setSelectedMonth] = useState(() =>
    moment().startOf("month").toDate()
  );

  const [branchSequenceData, setBranchSequenceData] = useState({});
  const [selectedBranchId, setSelectedBranchId] = useState(userDetails?.bid);
  const [selectedBranchName, setSelectedBranchName] = useState(null);
  const [selectedBranchLevel, setSelectedBranchLevel] = useState(null);

  const [country, setCountry] = useState({});
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [province, setProvince] = useState({});
  const [provinces, setProvinces] = useState([]);
  const [selectedProvince, setSelectedProvince] = useState(null);
  const [district, setDistrict] = useState({});
  const [districts, setDistricts] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [userBranch, setUserBranch] = useState({});
  const [branches, setBranches] = useState([]);
  const [selectedUserBranch, setSelectedUserBranch] = useState(null);
  const handleMonthChange = useCallback((date) => {
    if (!date) {
      return;
    }

    const monthStart = moment(date).startOf("month").toDate();
    const monthEnd = moment(date).endOf("month").toDate();

    setSelectedMonth(monthStart);
    setStartDate(monthStart);
    setEndDate(monthEnd);
  }, []);

  useEffect(() => {
    if (sDate || eDate) {
      const dateForMonth =
        sDate ??
        eDate ??
        moment().startOf("month").toDate();

      const monthStart = moment(dateForMonth).startOf("month").toDate();
      const monthEnd = moment(dateForMonth).endOf("month").toDate();

      setSelectedMonth(monthStart);
      setStartDate(monthStart);
      setEndDate(monthEnd);
    }
  }, [sDate, eDate]);

  useEffect(() => {
    if (!selectedBranchId) {
      return;
    }

     
    getAgents();
  }, [selectedBranchId]);

  useEffect(() => {
    setSelectedAgent(null);
  }, [selectedBranchId]);

  useEffect(() => {
    if (!selectedBranchId) {
      return;
    }

    const storedWeights = loadStoredWeightDistribution(selectedBranchId);

    if (!storedWeights) {
      return;
    }

    setWeightDistribution((prev) => {
      if (weightsAreEqual(prev, storedWeights)) {
        return prev;
      }

      return {
        ...INITIAL_WEIGHT_DISTRIBUTION,
        ...storedWeights,
      };
    });
  }, [selectedBranchId]);

  const persistWeightDistribution = useCallback((weights, branchId) => {
    if (typeof window === "undefined") {
      return;
    }

    if (!weights || typeof weights !== "object") {
      return;
    }

    const normalized = {
      cf: toNumberOrNull(numberOrDefault(weights.cf, null)),
      om: toNumberOrNull(numberOrDefault(weights.om, null)),
      fpt: toNumberOrNull(numberOrDefault(weights.fpt, null)),
    };

    const hasValues = Object.values(normalized).some(
      (value) => value !== null
    );

    if (!hasValues) {
      return;
    }

    try {
      const key = getPieStorageKey(branchId);
      const existing = loadStoredWeightDistribution(branchId);

      if (weightsAreEqual(existing, normalized)) {
        return;
      }

      localStorage.setItem(key, JSON.stringify(normalized));
    } catch (error) {
      console.warn("Failed to persist weight distribution", error);
    }
  }, []);

 

   

  useEffect(() => {
    if (baseUrl && token && selectedBranchId) {
      getBranchSequence();
    }
  }, [baseUrl, token, selectedBranchId]);

  useEffect(() => {
    if (
      selectedProvince !== undefined &&
      selectedProvince !== null &&
      userDetails["branchLevel"] !== 4
    ) {
      getDistricts(selectedProvince, 0);
    }
  }, [selectedProvince]);

  useEffect(() => {
    if (selectedDistrict !== undefined && selectedDistrict !== null) {
      getBranches(selectedDistrict);
    }
  }, [selectedDistrict]);

  useEffect(() => {
    if (
      !selectedCountry &&
      country &&
      typeof country === "object" &&
      country.id
    ) {
      setSelectedCountry(country.id);
    }
  }, [country, selectedCountry]);

  useEffect(() => {
    if (
      (selectedProvince === undefined || selectedProvince === null) &&
      Array.isArray(provinces) &&
      provinces.length > 0
    ) {
      const firstProvince = provinces[0];
      if (firstProvince && firstProvince.id) {
        setSelectedProvince(firstProvince.id);
      }
    } else if (
      (selectedProvince === undefined || selectedProvince === null) &&
      province &&
      typeof province === "object" &&
      province.id
    ) {
      setSelectedProvince(province.id);
    }
  }, [province, provinces, selectedProvince]);

  useEffect(() => {
    if (
      (selectedDistrict === undefined || selectedDistrict === null) &&
      Array.isArray(districts) &&
      districts.length > 0
    ) {
      const firstDistrict = districts[0];
      if (firstDistrict && firstDistrict.id) {
        setSelectedDistrict(firstDistrict.id);
      }
    } else if (
      (selectedDistrict === undefined || selectedDistrict === null) &&
      district &&
      typeof district === "object" &&
      district.id
    ) {
      setSelectedDistrict(district.id);
    }
  }, [district, districts, selectedDistrict]);

  useEffect(() => {
    if (
      (selectedUserBranch === undefined || selectedUserBranch === null) &&
      Array.isArray(branches) &&
      branches.length > 0
    ) {
      const firstBranch = branches[0];
      if (firstBranch && firstBranch.id) {
        setSelectedUserBranch(firstBranch.id);
        if (selectedBranchId !== firstBranch.id) {
          setSelectedBranchId(firstBranch.id);
          setSelectedBranchName(firstBranch.name || null);
          setSelectedBranchLevel(firstBranch.level || null);
        }
      }
    } else if (
      (selectedUserBranch === undefined || selectedUserBranch === null) &&
      userBranch &&
      typeof userBranch === "object" &&
      userBranch.id
    ) {
      setSelectedUserBranch(userBranch.id);
      if (selectedBranchId !== userBranch.id) {
        setSelectedBranchId(userBranch.id);
        setSelectedBranchName(userBranch.name || null);
        setSelectedBranchLevel(userBranch.level || null);
      }
    }
  }, [branches, selectedUserBranch, selectedBranchId, userBranch]);

  useEffect(() => {
    if (selectedBranchLevel === 4) {
      connectMQTT(onMessageArrived, onConnect, onConnectionLost);

      return () => {
        unsubscribeTopic(feedbackDashboardUpdate + `/${selectedBranchId}`);
        disconnectMQTT();
      };
    }
  }, [selectedBranchId, selectedBranchLevel]);

  const onMessageArrived = (message) => {
    setMessages((prev) => [...prev, message.payloadString]);
  };

  const onConnect = () => {
    subscribeTopic(feedbackDashboardUpdate + `/${selectedBranchId}`);
  };

  const onConnectionLost = (responseObject) => {
    if (responseObject.errorCode !== 0) {
      console.error("Connection lost:", responseObject.errorMessage);
    }
  };

  const applyPerformanceSummary = useCallback(
    (payload, options = { allowReset: true, updateTicketCounts: true }) => {
      const shouldReset = options?.allowReset !== false;
      const shouldUpdateTicketCounts = options?.updateTicketCounts !== false;

      const resetDashboard = () => {
        if (!shouldReset) {
          return;
        }

        setPerformanceMetrics({ ...INITIAL_PERFORMANCE_METRICS });
        setScoreCards({ ...INITIAL_SCORE_CARDS });
        setFinalBreakdown({ ...INITIAL_FINAL_BREAKDOWN });
        // setRankings([]);

        const storedWeights = loadStoredWeightDistribution(selectedBranchId);

        setWeightDistribution(
          storedWeights
            ? {
                ...INITIAL_WEIGHT_DISTRIBUTION,
                ...storedWeights,
              }
            : { ...INITIAL_WEIGHT_DISTRIBUTION }
        );
      };

      if (!payload || typeof payload !== "object") {
        resetDashboard();
        if (shouldReset || shouldUpdateTicketCounts) {
          setTicketCounts({ teller: null, total: null });
        }
        return;
      }

      const summary =
        payload?.performanceSummary ||
        payload?.performanceMetrics ||
        payload?.performance ||
        payload?.data ||
        payload;

      if (!summary || typeof summary !== "object") {
        resetDashboard();
        if (shouldReset || shouldUpdateTicketCounts) {
          setTicketCounts({ teller: null, total: null });
        }
        return;
      }

      const hasSummaryData = Object.keys(summary).some((key) => {
        const value = summary[key];
        if (value === null || value === undefined || value === "") {
          return false;
        }
        if (typeof value === "object" && !Array.isArray(value)) {
          return Object.keys(value).some(
            (nestedKey) =>
              value[nestedKey] !== null && value[nestedKey] !== undefined
          );
        }
        return true;
      });

      if (!hasSummaryData) {
        resetDashboard();
        if (shouldReset || shouldUpdateTicketCounts) {
          setTicketCounts({ teller: null, total: null });
        }
        return;
      }

      const customerSatisfactionSource = firstAvailable(
        summary.customerSatisfaction,
        summary.customerSatisfactionScore,
        summary.rating,
        summary.cf
      );

      const averageServedSource = firstAvailable(
        summary.averageServedTime,
        summary.avgServedTime,
        summary.ast
      );

      const averageWaitingSource = firstAvailable(
        summary.averageWaitingTime,
        summary.avgWaitingTime,
        summary.awt
      );

      const ticketCountSource = firstAvailable(
        summary.ticketCount,
        summary.totalServedCountTeller,
        summary.totalServedCount,
        summary.totalFeedbacks
      );

      const customerFeedbackScore = toNumberOrNull(
        firstAvailable(
          summary.customerFeedbackScore,
          summary.customerFeedback,
          summary.cf,
          summary.rating
        )
      );

      const operationalMetricsScore = toNumberOrNull(
        firstAvailable(
          summary.operationalMetricsScore,
          summary.operationalMetrics,
          summary.om,
          summary.to,
          summary.se,
          summary.wb
        )
      );

      const fptScore = toNumberOrNull(
        firstAvailable(
          summary.firstPointTicketScore,
          summary.firstPointTicket,
          summary.firstPointTickets,
          summary.fpt,
          summary.ftp
        )
      );

      const finalPerformanceScore = toNumberOrNull(
        firstAvailable(
          summary.finalPerformanceScore,
          summary.finalPerformance,
          summary.total,
          summary.fps,
          summary.finalScore
        )
      );

      const servedMinutes = timeStringToMinutes(averageServedSource);
      const waitingMinutes = timeStringToMinutes(averageWaitingSource);

      setPerformanceMetrics({
        customerSatisfaction: numberOrDefault(
          customerSatisfactionSource,
          null
        ),
        averageServedTime: numberOrDefault(servedMinutes, null),
        averageWaitingTime: numberOrDefault(waitingMinutes, null),
        ticketCount: numberOrDefault(ticketCountSource, null),
      });

      if (shouldUpdateTicketCounts) {
        setTicketCounts({
          teller: toNumberOrNull(summary.totalServedCountTeller),
          total: toNumberOrNull(summary.totalServedCount),
        });
      }

      const weightsSource =
        summary.weightDistribution ||
        summary.weights ||
        summary.distribution ||
        summary.weightage ||
        {};

      const weightCf = numberOrDefault(
        firstAvailable(
          weightsSource.customerFeedback,
          weightsSource.cf,
          weightsSource.CF
        ),
        null
      );
      const weightOm = numberOrDefault(
        firstAvailable(
          weightsSource.operationalMetrics,
          weightsSource.om,
          weightsSource.OM
        ),
        null
      );
      const weightFpt = numberOrDefault(
        firstAvailable(
          weightsSource.firstPointTicket,
          weightsSource.firstPointTickets,
          weightsSource.fpt,
          weightsSource.ftp,
          weightsSource.ftm,
          weightsSource.FPT,
          weightsSource.FTM
        ),
        null
      );

      const nextWeightDistribution = {
        cf: weightCf,
        om: weightOm,
        fpt: weightFpt,
      };

      const hasWeightValues = Object.values(nextWeightDistribution).some(
        (value) => value !== null
      );

      if (hasWeightValues) {
        persistWeightDistribution(nextWeightDistribution, selectedBranchId);
        setWeightDistribution(nextWeightDistribution);
      } else if (shouldReset) {
        const storedWeights = loadStoredWeightDistribution(selectedBranchId);
        if (storedWeights) {
          setWeightDistribution({
            ...INITIAL_WEIGHT_DISTRIBUTION,
            ...storedWeights,
          });
        } else {
          setWeightDistribution({ ...INITIAL_WEIGHT_DISTRIBUTION });
        }
      }

      setScoreCards({
        customerFeedback: customerFeedbackScore,
        operationalMetrics: operationalMetricsScore,
        feedbackPerTicket: fptScore,
        finalPerformance: finalPerformanceScore,
      });

      const breakdownSource =
        summary.finalBreakdown ||
        summary.breakdown ||
        summary?.scores?.breakdown ||
        summary?.performanceScores?.breakdown ||
        summary?.performance?.breakdown ||
        null;

      if (
        breakdownSource &&
        typeof breakdownSource === "object" &&
        Object.keys(breakdownSource).some(
          (key) =>
            breakdownSource[key] !== null && breakdownSource[key] !== undefined
        )
      ) {
        setFinalBreakdown({
          cf: numberOrDefault(
            firstAvailable(
              breakdownSource.customerFeedback,
              breakdownSource.cf,
              breakdownSource.CF
            ),
            null
          ),
          om: numberOrDefault(
            firstAvailable(
              breakdownSource.operationalMetrics,
              breakdownSource.om,
              breakdownSource.OM
            ),
            null
          ),
          fpt: numberOrDefault(
            firstAvailable(
              breakdownSource.firstPointTicket,
              breakdownSource.firstPointTickets,
              breakdownSource.fpt,
              breakdownSource.ftp,
              breakdownSource.ftm,
              breakdownSource.FPT,
              breakdownSource.FTM
            ),
            null
          ),
        });
      } else {
        const breakdownCf =
          weightCf !== null && customerFeedbackScore !== null
            ? (customerFeedbackScore * weightCf) / 100
            : null;
        const breakdownOm =
          weightOm !== null && operationalMetricsScore !== null
            ? (operationalMetricsScore * weightOm) / 100
            : null;
        const breakdownFpt =
          weightFpt !== null && fptScore !== null
            ? (fptScore * weightFpt) / 100
            : null;

        setFinalBreakdown({
          cf: breakdownCf,
          om: breakdownOm,
          fpt: breakdownFpt,
        });
      }
    },
    [persistWeightDistribution, selectedBranchId]
  );

   
  const fetchOperationalMetrics = useCallback(
    (agent) => {
      if (!agent || !baseUrl || !token) {
        return;
      }

      const agentId =
        agent?.id ??
        agent?.userId ??
        agent?.userID ??
        agent?.agentId ??
        agent?.agentID ??
        agent?.operatorId ??
        agent?.operatorID ??
        agent?.employeeId ??
        agent?.employeeID ??
        agent?.uuid ??
        agent?.username ??
        agent?.userName ??
        null;

      if (!agentId) {
        return;
      }

      setAllDataLoaded(false);

      axios({
        method: "POST",
        url: `${baseUrl}/feedback-performance/get-operational-metrics`,
        headers: {
          Authorization: token,
        },
        data: {
          branchId: selectedBranchId || userDetails.bid,
          tellerId :agentId,
          kioskId: agent?.kioskId ?? null,
          startDate: moment(startDate).format("YYYY-MM-DD"),
          endDate: moment(endDate).format("YYYY-MM-DD"),
        },
      })
        .then((res) => {
          const responseData = res?.data?.data ?? res?.data;
          applyPerformanceSummary(responseData, {
            allowReset: false,
            updateTicketCounts: true,
          });
        })
        .catch((error) => {
          console.log(error);

          if (error.response !== undefined) {
            toast.remove();
            toast.error(error.response.data.message, {
              position: "bottom-center",
            });
          } else {
            toast.remove();
            toast.error("Something went wrong", {
              position: "bottom-center",
            });
          }
        })
        .finally(() => {
          setAllDataLoaded(true);
        });
    },
    [
      applyPerformanceSummary,
      baseUrl,
      endDate,
      selectedBranchId,
      startDate,
      token,
      userDetails.bid,
    ]
  );

  useEffect(() => {
    if (!selectedAgent) {
      applyPerformanceSummary(null);
      return;
    }

    fetchOperationalMetrics(selectedAgent);
  }, [selectedAgent, fetchOperationalMetrics, applyPerformanceSummary]);

  useEffect(() => {
    applyPerformanceSummary(null);
  }, [selectedBranchId, startDate, endDate, applyPerformanceSummary]);

  const getSelectedKiosk = (selectedIdx) => {
    if (
      !queueAgentsData ||
      queueAgentsData.length === 0 ||
      selectedIdx === null ||
      selectedIdx === undefined
    ) {
      return;
    }

    if (selectedIdx === "") {
      setSelectedAgent(null);
      return;
    }

    if (selectedIdx === "All") {
      setKiosks(null);
      setSelectedAgent(null);
      return;
    }

    const index = Number(selectedIdx);

    if (Number.isNaN(index) || !queueAgentsData[index]) {
      setSelectedAgent(null);
      setKiosks(null);
      return;
    }

    const agent = queueAgentsData[index];
    const nextKiosks = [agent?.kioskId].filter(Boolean);

    setKiosks(nextKiosks.length > 0 ? nextKiosks : null);
    setSelectedAgent(agent);
  };

  const getAgents = () => {
    if (!baseUrl || !token) {
      return;
    }

    const branchIds = [selectedBranchId || userDetails.bid];

    axios({
      method: "POST",
      url: `${baseUrl}/feedback-performance/get-users/${branchIds}`,
      headers: {
        Authorization: token,
      },
      data: {
        branchIds: branchIds,
      },
    })
      .then((res) => {
        const data = res.data.data;
        setQueueAgentsData(data);
        setSelectedAgent(null);
      })
      .catch((error) => {
        console.log(error);

        if (error.response !== undefined) {
          toast.remove();
          toast.error(error.response.data.message, {
            position: "bottom-center",
          });
        } else {
          toast.remove();
          toast.error("Something went wrong", {
            position: "bottom-center",
          });
        }
      });
  };

  const getBranchSequence = () => {
    axios({
      method: "GET",
      url: `${baseUrl}/branch/get-sequence/branch/${selectedBranchId}`,
      headers: {
        Authorization: token,
      },
    })
      .then((res) => {
        const data = res.data.data;
        setBranchSequenceData(data);

        if (data) {
          if (data.level !== undefined) {
            setSelectedBranchLevel(data.level);
          }
          if (data.countryId !== undefined) {
            setSelectedCountry(data.countryId);
            getCountry(data.countryId);
            getProvinces(data.countryId, data.provinceId);
          }

          if (data.provinceId !== undefined) {
            setSelectedProvince(data.provinceId);
            getDistricts(data.provinceId, data.districtId);
          }

          if (data.districtId !== undefined) {
            setSelectedDistrict(data.districtId);
            getBranches(data.districtId);
          }

          if (data.branchId !== undefined) {
            setSelectedUserBranch(data.branchId);
          }
        }
      })
      .catch((error) => {
        console.log("getBranchSequence error");
        console.log(error);

        if (error.response !== undefined) {
          toast.remove();
          toast.error(error.response.data.message, {
            position: "bottom-center",
          });
        } else {
          toast.remove();
          toast.error("Something went wrong", {
            position: "bottom-center",
          });
        }
      });
  };

  const getCountry = (countryId) => {
    axios({
      method: "GET",
      url: `${baseUrl}/branch/get-branch/${countryId}`,
      headers: {
        Authorization: token,
      },
    })
      .then((res) => {
        const data = res.data.data;
        setCountry(data);
      })
      .catch((error) => {
        console.log("getCountry error");
        console.log(error);

        if (error.response !== undefined) {
          toast.remove();
          toast.error(error.response.data.message, {
            position: "bottom-center",
          });
        } else {
          toast.remove();
          toast.error("Something went wrong", {
            position: "bottom-center",
          });
        }
      });
  };

  const getProvinces = (countryId, provinceId) => {
    let url = ``;

    if (userDetails["branchLevel"] === 1) {
      url = `${baseUrl}/branch/get-all/parent-branch/${countryId}`;
    } else if (
      userDetails["branchLevel"] === 3 ||
      userDetails["branchLevel"] === 4
    ) {
      url = `${baseUrl}/branch/get-branch/${provinceId}`;
    } else {
      url = `${baseUrl}/branch/get-branch/${userDetails["bid"]}`;
    }

    axios({
      method: "GET",
      url: url,
      headers: {
        Authorization: token,
      },
    })
      .then((res) => {
        const data = res.data.data;
        if (userDetails["branchLevel"] === 1) {
          setProvinces(data);
        } else {
          setProvince(data);
        }
      })
      .catch((error) => {
        console.log("getProvinces error");
        console.log(error);

        if (error.response !== undefined) {
          toast.remove();
          toast.error(error.response.data.message, {
            position: "bottom-center",
          });
        } else {
          toast.remove();
          toast.error("Something went wrong", {
            position: "bottom-center",
          });
        }
      });
  };

  const getDistricts = (provinceId, districtId) => {
    let url = ``;

    if (userDetails["branchLevel"] === 1 || userDetails["branchLevel"] === 2) {
      url = `${baseUrl}/branch/get-all/parent-branch/${provinceId}`;
    } else if (userDetails["branchLevel"] === 4) {
      url = `${baseUrl}/branch/get-branch/${districtId}`;
    } else {
      url = `${baseUrl}/branch/get-branch/${userDetails["bid"]}`;
    }

    axios({
      method: "GET",
      url: url,
      headers: {
        Authorization: token,
      },
    })
      .then((res) => {
        const data = res.data.data;

        if (
          userDetails["branchLevel"] === 1 ||
          userDetails["branchLevel"] === 2
        ) {
          setDistricts(data);
        } else {
          setDistrict(data);
        }
      })
      .catch((error) => {
        console.log("getDistricts error");
        console.log(error);

        if (error.response !== undefined) {
          toast.remove();
          toast.error(error.response.data.message, {
            position: "bottom-center",
          });
        } else {
          toast.remove();
          toast.error("Something went wrong", {
            position: "bottom-center",
          });
        }
      });
  };

  const getBranches = (districtId) => {
    let url = ``;

    if (userDetails["branchLevel"] === 4) {
      url = `${baseUrl}/branch/get-branch/${userDetails["bid"]}`;
    } else {
      url = `${baseUrl}/branch/get-all/parent-branch/${districtId}`;
    }

    axios({
      method: "GET",
      url: url,
      headers: {
        Authorization: token,
      },
    })
      .then((res) => {
        const data = res.data.data;

        if (userDetails["branchLevel"] === 4) {
          setUserBranch(data);
        } else if (data !== null) {
          setBranches(data);
        }
      })
      .catch((error) => {
        console.log("getBranches error");
        console.log(error);

        if (error.response !== undefined) {
          toast.remove();
          toast.error(error.response.data.message, {
            position: "bottom-center",
          });
        } else {
          toast.remove();
          toast.error("Something went wrong", {
            position: "bottom-center",
          });
        }
      });
  };

  const pieSeries = useMemo(
    () => [
      numberOrDefault(weightDistribution.cf, 0),
      numberOrDefault(weightDistribution.om, 0),
      numberOrDefault(weightDistribution.fpt, 0),
    ],
    [weightDistribution]
  );

  const pieOptions = useMemo(
    () => ({
      chart: {
        type: "pie",
      },
        labels: ["CF", "OM", "FPT"], colors: ["#F5A927", "#CC983F", "#D67427"],

      legend: {
          position: "right",
        fontSize: "14px",
      },
      dataLabels: {
        formatter: (val) => `${val.toFixed(1)}%`,
      },
    }),
    []
  );

  const finalScoreEquation = useMemo(() => {
    const formatPart = (value) => {
      const numeric = toNumberOrNull(numberOrDefault(value, null));
      if (numeric === null) {
        return "-";
      }
      return Number(numeric).toFixed(1);
    };

    const cfPart = formatPart(finalBreakdown.cf);
    const omPart = formatPart(finalBreakdown.om);
    const fptPart = formatPart(finalBreakdown.fpt);
    const totalNumeric = toNumberOrNull(
      numberOrDefault(scoreCards.finalPerformance, null)
    );
    const totalPart =
      totalNumeric === null ? "-" : Number(totalNumeric).toFixed(1);

    if (cfPart === "-" && omPart === "-" && fptPart === "-" && totalPart === "-") {
      return "-";
    }

    return `${cfPart} + ${omPart} + ${fptPart} = ${totalPart}`;
  }, [finalBreakdown, scoreCards]);

  const feedbackPerTicketScore = useMemo(() => {
    const weightedValue = toNumberOrNull(
      numberOrDefault(finalBreakdown.fpt, null)
    );
    if (weightedValue !== null) {
      return weightedValue;
    }

    const rawScore = toNumberOrNull(
      numberOrDefault(scoreCards.feedbackPerTicket, null)
    );
    const weight = toNumberOrNull(
      numberOrDefault(weightDistribution.fpt, null)
    );

    if (rawScore === null || weight === null) {
      return null;
    }

    return (rawScore * weight) / 100;
  }, [finalBreakdown.fpt, scoreCards.feedbackPerTicket, weightDistribution.fpt]);

  const renderWeightValue = (value) => {
    const numeric = numberOrDefault(value, null);
    return numeric === null ? "-" : `${numeric}%`;
  };

  const metricCards = useMemo(
    () => [
      {
        key: "csat",
        label: "Customer Satisfaction",
          value: performanceMetrics.customerSatisfaction,
          digits: 1,
        helper: "",
      },
      {
        key: "avg-served",
        label: "Avg. Served Time",
          value: performanceMetrics.averageServedTime,
          digits: 1,
        helper: "min",
      },
      {
        key: "avg-waiting",
        label: "Avg. Waiting Time",
          value: performanceMetrics.averageWaitingTime,
          digits: 1,
        helper: "min",
      },
      {
        key: "ticket-count",
        label: "Ticket Count",
          isTicketCount: true,
          teller: ticketCounts.teller,
          total: ticketCounts.total,
        helper: "",
      },
    ],
      [performanceMetrics, ticketCounts]
  );

  const pieHasData = useMemo(
    () => pieSeries.some((value) => Number(value) > 0),
    [pieSeries]
  );

  return (
    <>
      <div>
        <Toaster />
      </div>

      <div className="block-header block-header-padding">
        <div className="row clearfix">
          <div className="col-md-6 col-sm-12">
            <h1>Performance Dashboard</h1>
          </div>
        </div>
      </div>

      <div className="container-fluid branch-dashboard mt-4">
        {userDetails["branchLevel"] !== 4 ? (
          <div className="row clearfix">
            <div className="col-md-12 col-sm-8 p-l-5 p-r-5 mb-4">
              <div className="card">
                <div className="body">
                  <div className="row">
                    <div className="col-md-12 col-sm-12 p-l-5 p-r-5">
                      <Accordion className="accordion" id="performance-accordion">
                        <div>
                          <div className="card-header" id="performanceHeading">
                            <h5 className="mb-0">
                              <Accordion.Toggle
                                as={Button}
                                variant="link"
                                eventKey="0"
                                className={
                                  selectedBranchLevel === 4
                                    ? "acc-color-branch"
                                    : selectedBranchLevel === 3
                                    ? "acc-color-district"
                                    : selectedBranchLevel === 2
                                    ? "acc-color-province"
                                    : "acc-color-country"
                                }
                              >
                                {branchSequenceData.name}
                                <div className="float-right">
                                  <i className="fa fa-chevron-circle-down"></i>
                                </div>
                              </Accordion.Toggle>
                            </h5>
                          </div>
                          <Accordion.Collapse eventKey="0" className="collapse">
                            <div className="body">
                              <div className="row clearfix">
                                <div className="col-lg-3 col-md-6 col-sm-12">
                                  <div className="card">
                                    <div className="taskboard b-cyan progress_task">
                                      <h6 className="font300 mb-3">Country</h6>
                                      <div
                                        className="dd"
                                        data-plugin="nestable"
                                      >
                                        {userDetails["branchLevel"] === 1 ? (
                                          <div
                                            className={`dd-handle align-items-center ${
                                              selectedCountry === country.id
                                                ? "active"
                                                : ""
                                            }`}
                                            onClick={() => {
                                              setSelectedCountry(country.id);
                                              setSelectedProvince(null);
                                              setSelectedDistrict(null);
                                              setSelectedUserBranch(null);
                                              setDistricts([]);
                                              setBranches([]);
                                            }}
                                          >
                                            <div className="d-flex justify-content-between">
                                              <div>{country.name}</div>
                                              <div
                                                className={
                                                  selectedBranchId === country.id
                                                    ? "btn btn-prymary select-btn external-link-active"
                                                    : "btn btn-prymary select-btn"
                                                }
                                                onClick={() => {
                                                  setSelectedBranchId(country.id);
                                                  setSelectedBranchName(
                                                    country.name
                                                  );
                                                  setSelectedBranchLevel(
                                                    country.level
                                                  );
                                                }}
                                              >
                                                <i className="fa fa-external-link float-left"></i>
                                              </div>
                                            </div>
                                          </div>
                                        ) : (
                                          <div
                                            className="dd-handle align-items-center"
                                            style={{ cursor: "default" }}
                                          >
                                            <div className="d-flex justify-content-between">
                                              <div>{country.name}</div>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="col-lg-3 col-md-6 col-sm-12">
                                  <div className="card">
                                    <div className="taskboard b-cyan progress_task">
                                      <h6 className="font300 mb-3">Province</h6>
                                      <div
                                        className="dd"
                                        data-plugin="nestable"
                                      >
                                        {provinces !== null ? (
                                          <>
                                            {userDetails["branchLevel"] === 1 ? (
                                              <>
                                                {provinces.map((data, i) => (
                                                  <div
                                                    className={`dd-handle align-items-center ${
                                                      selectedProvince === data.id
                                                        ? "active"
                                                        : ""
                                                    }`}
                                                    key={i}
                                                    onClick={() => {
                                                      setSelectedProvince(data.id);
                                                      setSelectedDistrict(null);
                                                      setSelectedUserBranch(null);
                                                      setBranches([]);
                                                    }}
                                                  >
                                                    <div className="d-flex justify-content-between">
                                                      <div>{data.name}</div>
                                                      <div
                                                        className={
                                                          selectedBranchId === data.id
                                                            ? "btn btn-prymary select-btn external-link-active"
                                                            : "btn btn-prymary select-btn"
                                                        }
                                                        onClick={() => {
                                                          setSelectedBranchId(
                                                            data.id
                                                          );
                                                          setSelectedBranchName(
                                                            data.name
                                                          );
                                                          setSelectedBranchLevel(
                                                            data.level
                                                          );
                                                        }}
                                                      >
                                                        <i className="fa fa-external-link float-left"></i>
                                                      </div>
                                                    </div>
                                                  </div>
                                                ))}
                                              </>
                                            ) : userDetails["branchLevel"] === 2 ? (
                                              <div
                                                className={`dd-handle align-items-center ${
                                                  selectedProvince === province.id
                                                    ? "active"
                                                    : ""
                                                }`}
                                                onClick={() => {
                                                  setSelectedProvince(province.id);
                                                  setSelectedDistrict(null);
                                                  setSelectedUserBranch(null);
                                                  setBranches([]);
                                                }}
                                              >
                                                <div className="d-flex justify-content-between">
                                                  <div>{province.name}</div>
                                                  <div
                                                    className={
                                                      selectedBranchId === province.id
                                                        ? "btn btn-prymary select-btn external-link-active"
                                                        : "btn btn-prymary select-btn"
                                                    }
                                                    onClick={() => {
                                                      setSelectedBranchId(
                                                        province.id
                                                      );
                                                      setSelectedBranchName(
                                                        province.name
                                                      );
                                                      setSelectedBranchLevel(
                                                        province.level
                                                      );
                                                    }}
                                                  >
                                                    <i className="fa fa-external-link float-left"></i>
                                                  </div>
                                                </div>
                                              </div>
                                            ) : (
                                              <div
                                                className="dd-handle align-items-center"
                                                style={{ cursor: "default" }}
                                              >
                                                <div className="d-flex justify-content-between">
                                                  <div>{province.name}</div>
                                                </div>
                                              </div>
                                            )}
                                          </>
                                        ) : (
                                          <div className="dd-handle align-items-center">
                                            <div className="d-flex justify-content-between">
                                              <div>No provinces yet...</div>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                <div className="col-lg-3 col-md-6 col-sm-12">
                                  <div className="card">
                                    <div className="taskboard b-cyan progress_task">
                                      <h6 className="font300 mb-3">District</h6>
                                      <div
                                        className="dd"
                                        data-plugin="nestable"
                                      >
                                        {userDetails["branchLevel"] === 1 ||
                                        userDetails["branchLevel"] === 2 ? (
                                          <>
                                            {districts !== null ? (
                                              <>
                                                {districts.length > 0 ? (
                                                  <>
                                                    {districts.map((data, i) => (
                                                      <div
                                                        className={`dd-handle align-items-center ${
                                                          selectedDistrict === data.id
                                                            ? "active"
                                                            : ""
                                                        }`}
                                                        key={i}
                                                        onClick={() => {
                                                          setSelectedDistrict(data.id);
                                                          setSelectedUserBranch(null);
                                                        }}
                                                      >
                                                        <div className="d-flex justify-content-between">
                                                          <div>{data.name}</div>
                                                          <div
                                                            className={
                                                              selectedBranchId === data.id
                                                                ? "btn btn-prymary select-btn external-link-active"
                                                                : "btn btn-prymary select-btn"
                                                            }
                                                            onClick={() => {
                                                              setSelectedBranchId(
                                                                data.id
                                                              );
                                                              setSelectedBranchName(
                                                                data.name
                                                              );
                                                              setSelectedBranchLevel(
                                                                data.level
                                                              );
                                                            }}
                                                          >
                                                            <i className="fa fa-external-link float-left"></i>
                                                          </div>
                                                        </div>
                                                      </div>
                                                    ))}
                                                  </>
                                                ) : (
                                                  <div className="dd-handle align-items-center">
                                                    <div className="d-flex justify-content-between">
                                                      <div>Select Province</div>
                                                    </div>
                                                  </div>
                                                )}
                                              </>
                                            ) : (
                                              <div className="dd-handle align-items-center">
                                                <div className="d-flex justify-content-between">
                                                  <div>No districts yet...</div>
                                                </div>
                                              </div>
                                            )}
                                          </>
                                        ) : userDetails["branchLevel"] === 3 ? (
                                          <div
                                            className={`dd-handle align-items-center ${
                                              selectedDistrict === district.id
                                                ? "active"
                                                : ""
                                            }`}
                                            onClick={() => {
                                              setSelectedDistrict(district.id);
                                              setSelectedUserBranch(null);
                                            }}
                                          >
                                            <div className="d-flex justify-content-between">
                                              <div>{district.name}</div>
                                              <div
                                                className={
                                                  selectedBranchId === district.id
                                                    ? "btn btn-prymary select-btn external-link-active"
                                                    : "btn btn-prymary select-btn"
                                                }
                                                onClick={() => {
                                                  setSelectedBranchId(district.id);
                                                  setSelectedBranchName(
                                                    district.name
                                                  );
                                                  setSelectedBranchLevel(
                                                    district.level
                                                  );
                                                }}
                                              >
                                                <i className="fa fa-external-link float-left"></i>
                                              </div>
                                            </div>
                                          </div>
                                        ) : (
                                          <div
                                            className="dd-handle align-items-center"
                                            style={{ cursor: "default" }}
                                          >
                                            <div className="d-flex justify-content-between">
                                              <div>{district.name}</div>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="col-lg-3 col-md-6 col-sm-12">
                                  <div className="card">
                                    <div className="taskboard b-cyan progress_task">
                                      <h6 className="font300 mb-3">Branch</h6>
                                      <div
                                        className="dd"
                                        data-plugin="nestable"
                                      >
                                        {userDetails["branchLevel"] === 1 ||
                                        userDetails["branchLevel"] === 2 ||
                                        userDetails["branchLevel"] === 3 ? (
                                          <>
                                            {branches !== null ? (
                                              <>
                                                {branches.length > 0 ? (
                                                  <>
                                                    {branches.map((data, i) => (
                                                      <div
                                                        className={`dd-handle align-items-center ${
                                                          selectedUserBranch === data.id
                                                            ? "active"
                                                            : ""
                                                        }`}
                                                        key={i}
                                                        onClick={() => {
                                                          setSelectedUserBranch(data.id);
                                                        }}
                                                      >
                                                        <div className="d-flex justify-content-between">
                                                          <div>{data.name}</div>
                                                          <div
                                                            className={
                                                              selectedBranchId === data.id
                                                                ? "btn btn-prymary select-btn external-link-active"
                                                                : "btn btn-prymary select-btn"
                                                            }
                                                            onClick={() => {
                                                              setSelectedBranchId(
                                                                data.id
                                                              );
                                                              setSelectedBranchName(
                                                                data.name
                                                              );
                                                              setSelectedBranchLevel(
                                                                data.level
                                                              );
                                                            }}
                                                          >
                                                            <i className="fa fa-external-link float-left"></i>
                                                          </div>
                                                        </div>
                                                      </div>
                                                    ))}
                                                  </>
                                                ) : (
                                                  <div className="dd-handle align-items-center">
                                                    <div className="d-flex justify-content-between">
                                                      <div>Select District</div>
                                                    </div>
                                                  </div>
                                                )}
                                              </>
                                            ) : (
                                              <div className="dd-handle align-items-center">
                                                <div className="d-flex justify-content-between">
                                                  <div>No branches yet...</div>
                                                </div>
                                              </div>
                                            )}
                                          </>
                                        ) : userDetails["branchLevel"] === 4 ? (
                                          <div
                                            className={`dd-handle align-items-center ${
                                              selectedUserBranch === userBranch.id
                                                ? "active"
                                                : ""
                                            }`}
                                            onClick={() => {
                                              setSelectedUserBranch(userBranch.id);
                                              setSelectedBranchId(userBranch.id);
                                              setSelectedBranchName(userBranch.name);
                                              setSelectedBranchLevel(userBranch.level);
                                            }}
                                          >
                                            <div className="d-flex justify-content-between">
                                              <div>{userBranch.name}</div>
                                              <div
                                                className={
                                                  selectedBranchId === userBranch.id
                                                    ? "btn btn-prymary select-btn external-link-active"
                                                    : "btn btn-prymary select-btn"
                                                }
                                              >
                                                <i className="fa fa-external-link float-left"></i>
                                              </div>
                                            </div>
                                          </div>
                                        ) : (
                                          <div
                                            className="dd-handle align-items-center"
                                            style={{ cursor: "default" }}
                                          >
                                            <div className="d-flex justify-content-between">
                                              <div>{userBranch.name}</div>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Accordion.Collapse>
                        </div>
                      </Accordion>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        <div className="row clearfix align-items-stretch mb-2">
          {queueAgentsData !== null && queueAgentsData.length > 0 ? (
            <div className="col-lg-6 col-md-12 col-sm-12 p-l-5 p-r-5 mb-4">
              <div className="card h-100">
                <div className="body">
                  <label
                    htmlFor="performanceCounterSelect"
                    className="text-muted d-block mb-2"
                  >
                        Agent
                  </label>
                  <select
                    className="custom-select modal-select-field select-branch-dropdown"
                    id="performanceCounterSelect"
                    defaultValue=""
                    onChange={(e) => {
                      getSelectedKiosk(e.target.value);
                    }}
                  >
                    <option value="" disabled hidden>
                          Select Agent
                    </option>
                    <option value="All">Select Agent</option>
                        {queueAgentsData.map((item, index) => (
                      <option value={index} key={index}>
                        {item.firstName} {" "} {item.lastName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          ) : null}
          <div
            className={`${
              queueAgentsData !== null && queueAgentsData.length > 0
                ? "col-lg-6"
                : "col-lg-12"
            } col-md-12 col-sm-12 p-l-5 p-r-5 mb-4`}
          >
            <div className="card h-100">
              <div className="body">
                <label className="text-muted d-block mb-2">Month</label>
                <DatePicker
                  className="input-sm form-control"
                  selected={selectedMonth}
                  onChange={handleMonthChange}
                  dateFormat="MMMM yyyy"
                  showMonthYearPicker
                />
              </div>
            </div>
          </div>
        </div>

        <div className="row clearfix mb-3">
          <div className="col-lg-12 d-flex flex-wrap">
            {metricCards.map((card) => (
              <div
                key={card.key}
                className="col-lg-3 col-md-6 col-sm-12 p-l-5 p-r-10 mb-3"
              >
                {allDataLoaded ? (
                  <div className="card h-100">
                    <div className="body d-flex flex-column justify-content-center align-items-start p-4">
                      <p className="text-muted mb-1">{card.label}</p>
                      <div className="d-flex align-items-baseline">
                          <h3 className="mb-0 mr-2">
                              {card.isTicketCount ? (
                                  <AnimatedTicketCount
                                      teller={card.teller}
                                      total={card.total}
                                  />
                              ) : (
                                  <AnimatedNumber
                                      value={card.value}
                                      digits={card.digits}
                                      suffix={card.suffix || ""}
                                  />
                              )}
                          </h3>
                        {card.helper ? (
                          <span className="text-muted">{card.helper}</span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ) : (
                  <ShimmerThumbnail height={120} />
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="row clearfix">
          <div className="col-lg-4 col-md-12 col-sm-12 p-l-5 p-r-5 mb-4">
            <div className="card h-100">
              <div className="body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="my-0">Weight Distribution</h6>
                </div>
                {allDataLoaded || pieHasData ? (
                  pieHasData ? (
                    <ReactApexChart
                      options={pieOptions}
                      series={pieSeries}
                      type="pie"
                      height="195px"
                    />
                  ) : (
                    <div className="text-center py-5 text-muted">
                      No weight distribution data
                    </div>
                  )
                ) : (
                  <ShimmerThumbnail height={240} />
                )}
                {allDataLoaded || pieHasData ? (
                  <div className="d-flex justify-content-around mt-3">
                    <div className="text-center">
                        <span className="badge badge-warning mr-1">CF</span>
                      <span>{renderWeightValue(weightDistribution.cf)}</span>
                    </div>
                    <div className="text-center">
                        <span className="badge badge-warning mr-1">OM</span>
                      <span>{renderWeightValue(weightDistribution.om)}</span>
                    </div>
                    <div className="text-center">
                        <span className="badge badge-warning mr-1">FPT</span>
                      <span>{renderWeightValue(weightDistribution.fpt)}</span>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <div className="col-lg-8 col-md-12 col-sm-12 p-l-5 p-r-5 mb-4">
            <div className="row">
              <div className="col-lg-6 col-md-6 col-sm-12 mb-4">
                <div className="card h-100">
                  <div className="body d-flex flex-column justify-content-between">
                    <div>
                      <h6 className="my-0">Customer Feedback Score</h6>
                      <p className="text-muted mb-2">
                        Latest customer feedback performance
                      </p>
                    </div>
                    {allDataLoaded ? (
                      <h2 className="mb-0">
                        {formattedValue(
                          finalBreakdown.cf ?? scoreCards.customerFeedback,
                          1,
                          ""
                        )}
                      </h2>
                    ) : (
                      <ShimmerThumbnail height={80} />
                    )}
                  </div>
                </div>
              </div>
              <div className="col-lg-6 col-md-6 col-sm-12 mb-4">
                <div className="card h-100">
                  <div className="body d-flex flex-column justify-content-between">
                    <div>
                      <h6 className="my-0">Operational Metrics Score</h6>
                      <p className="text-muted mb-2">
                        Operational efficiency across counters
                      </p>
                    </div>
                    {allDataLoaded ? (
                      <h2 className="mb-0">
                        {formattedValue(
                          finalBreakdown.om ?? scoreCards.operationalMetrics,
                          1,
                          ""
                        )}
                      </h2>
                    ) : (
                      <ShimmerThumbnail height={80} />
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-lg-6 col-md-6 col-sm-12 mb-4">
                <div className="card h-100">
                  <div className="body d-flex flex-column justify-content-between">
                    <div>
                      <h6 className="my-0">Feedback Per Ticket Handled</h6>
                      <p className="text-muted mb-2">
                        Feedback contribution per handled tickets
                      </p>
                    </div>
                    {allDataLoaded ? (
                      <h2 className="mb-0">
                        {formattedValue(feedbackPerTicketScore, 1, "")}
                      </h2>
                    ) : (
                      <ShimmerThumbnail height={80} />
                    )}
                    <p className="text-muted mb-3">
                      
                    </p>
                  </div>
                </div>
              </div>

              <div className="col-lg-6 col-md-6 col-sm-12 mb-4">
                <div className="card h-100">
                  <div className="body">
                    <h6 className="my-0 mb-3">Final Performance Score</h6>
                    {allDataLoaded ? (
                      <>
                        <h2 className="mb-2">
                          {formattedValue(scoreCards.finalPerformance, 1, "")}
                          <small style={{ fontSize: "14px" }}> /100</small>
                        </h2>
                        <p className="text-muted mb-0">
                          <>{finalScoreEquation}</>
                        </p>
                      </>
                    ) : (
                      <ShimmerThumbnail height={100} />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </>
  );
};

export default FeedbackPerformanceDashboard;

